import dayjs from "dayjs";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import { ReactComponent as NoMessagesImage } from "../../../../../../assets/images/woman-computer.svg";
import { createColumn, Table } from "../../../../../../components/Table";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import { FitbitSleepChartRow } from "../FitbitSleepChart/FitbitSleepChart";
import {
  ImageContainer,
  NoContentMessage,
  TableContainer,
} from "./FitbitSleepTable.styles";

type FitbitSleepTableProps = {
  data: FitbitSleepChartRow[];
  isPinnedGraph?: boolean;
  onRowClick?: (row: FitbitSleepChartRow) => void;
};

interface IFitbitSleepTableRow {
  id: number;
  date: string;
  total: string;
  awake: string;
  rem: string;
  light: string;
  deep: string;
  sessionId?: string;
}

const minutesToHoursLabel = (m?: number | null) =>
  m != null && m > 0 ? formatHoursAndMinutes(m / 60) : "-";

export function FitbitSleepTable({
  data,
  isPinnedGraph,
  onRowClick,
}: FitbitSleepTableProps) {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <ImageContainer>
        <NoMessagesImage width={181} height={158} />
        <NoContentMessage>
          {t("patientPage_measurementModal_sleep_tableEmpty")}
        </NoContentMessage>
      </ImageContainer>
    );
  }

  const tableData: IFitbitSleepTableRow[] = data.map((row, index) => ({
    id: index,
    date: dayjs(row.timeValue).format("MMM D, YYYY"),
    total: formatHoursAndMinutes(row.hoursAsleep),
    awake: minutesToHoursLabel(row.stageSummary?.awakeMinutes),
    rem: minutesToHoursLabel(row.stageSummary?.remMinutes),
    light: minutesToHoursLabel(row.stageSummary?.lightMinutes),
    deep: minutesToHoursLabel(row.stageSummary?.deepMinutes),
    sessionId: row.sessionId,
  }));

  const columns = [
    createColumn({
      keyName: "date",
      header: () => i18next.t("patientPage_columns_date"),
      cell: (info) => info.renderValue(),
      size: 150,
    }),
    createColumn({
      keyName: "total",
      header: () => i18next.t("patientPage_columns_total"),
      cell: (info) => info.renderValue(),
      size: 100,
    }),
    createColumn({
      keyName: "awake",
      header: () => i18next.t("patientPage_columns_awake"),
      cell: (info) => info.renderValue(),
      size: 100,
    }),
    createColumn({
      keyName: "rem",
      header: () => i18next.t("patientPage_columns_rem"),
      cell: (info) => info.renderValue(),
      size: 100,
    }),
    createColumn({
      keyName: "light",
      header: () => i18next.t("patientPage_columns_light"),
      cell: (info) => info.renderValue(),
      size: 120,
    }),
    createColumn({
      keyName: "deep",
      header: () => i18next.t("patientPage_columns_deep"),
      cell: (info) => info.renderValue(),
      size: 120,
    }),
  ];

  const indexBySessionId = new Map(
    data.map((row, index) => [row.sessionId, index] as const)
  );

  const handleRowClick = (rowContent: IFitbitSleepTableRow) => {
    if (!onRowClick || !rowContent.sessionId) return;
    const originalIndex = indexBySessionId.get(rowContent.sessionId);
    if (originalIndex == null) return;
    onRowClick(data[originalIndex]);
  };

  return (
    <TableContainer isPinnedGraph={isPinnedGraph}>
      <Table
        columns={columns}
        data={tableData}
        onRowClick={onRowClick ? handleRowClick : undefined}
      />
    </TableContainer>
  );
}
